import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, CheckCircle, Activity, Film, Image as ImageIcon } from 'lucide-react';
import EvidenceVideoPlayer from '../../features/project-management/components/EvidenceVideoPlayer';
import { isEvidenceVideo } from '../../lib/imageUrl';

export default function GuestMonitoring() {
  const { token } = useParams<{ token: string }>();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        // Request to public API without auth headers
        const apiUrl = import.meta.env.VITE_API_URL || 'https://indoteknisi.net/api';
        const res = await axios.get(`${apiUrl}/v1/guest/manpro/project/${token}`);
        if (res.data.status === 'success') {
          setProject(res.data.data);
        } else {
          setError(res.data.message || 'Proyek tidak ditemukan.');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Gagal memuat data proyek.');
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      fetchProject();
    }
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Memuat Data Proyek...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white p-8 rounded-3xl shadow-xl max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Activity size={40} />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-800 mb-2">Akses Ditolak</h2>
          <p className="text-slate-600 leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div>
              <p className="text-sm font-extrabold text-blue-600 uppercase tracking-wider mb-1">
                Laporan Progres (Guest View)
              </p>
              <h1 className="text-3xl font-extrabold text-slate-900">{project.name}</h1>
              <span className="inline-block mt-3 px-3 py-1 bg-slate-100 text-slate-700 font-bold rounded-lg text-sm capitalize">
                Status: {project.status.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500 font-medium">Total Progres</p>
              <div className="text-4xl font-black text-emerald-600 mt-1">{project.overall_progress}%</div>
            </div>
          </div>
          
          <div className="mt-8">
            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
              <div 
                className="bg-emerald-500 h-3 rounded-full transition-all duration-1000" 
                style={{ width: `${project.overall_progress}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Milestones & Tasks */}
        <div className="space-y-6">
          <h2 className="text-xl font-extrabold text-slate-800">Detail Pekerjaan (WBS)</h2>
          
          {project.milestones.map((milestone: any) => {
            // Calculate milestone progress
            const mProgress = milestone.tasks.reduce((sum: number, t: any) => sum + ((t.progress * t.weight) / 100), 0);
            
            return (
              <div key={milestone.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <div className="font-extrabold text-slate-800 text-lg">{milestone.label}</div>
                  <div className="text-sm font-bold text-slate-500">
                    Progres: <span className="text-emerald-600">{mProgress.toFixed(2)}%</span>
                  </div>
                </div>
                
                <div className="divide-y divide-slate-100">
                  {milestone.tasks.map((task: any) => (
                    <div key={task.id} className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-3">
                          {task.progress === 100 ? (
                            <CheckCircle className="text-emerald-500" size={20} />
                          ) : (
                            <Clock className="text-amber-500" size={20} />
                          )}
                          <span className="font-bold text-slate-700">{task.name}</span>
                        </div>
                        <div className="font-mono font-bold text-slate-600">{task.progress}%</div>
                      </div>
                      
                      <div className="w-full bg-slate-100 rounded-full h-2 mb-6">
                        <div 
                          className={`h-2 rounded-full transition-all ${task.progress === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} 
                          style={{ width: `${task.progress}%` }}
                        ></div>
                      </div>

                      {/* Evidences — V8: foto + video aman */}
                      {task.evidences && task.evidences.length > 0 && (
                        <div className="mt-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                          <h4 className="text-xs font-extrabold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <ImageIcon size={14} /> Bukti Pekerjaan
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            {task.evidences.map((evidence: any) => {
                              const url = evidence.file_url || evidence.url || '';
                              const video =
                                evidence.type === 'video' ||
                                isEvidenceVideo({
                                  type: evidence.type,
                                  url,
                                  path: evidence.path,
                                });

                              if (video) {
                                return (
                                  <div
                                    key={evidence.id}
                                    className="rounded-lg overflow-hidden border border-slate-300 bg-slate-900"
                                  >
                                    <EvidenceVideoPlayer url={url} className="rounded-none" />
                                    <div className="flex items-center gap-1.5 bg-slate-800 px-2 py-1.5 text-[10px] font-bold text-white">
                                      <Film size={12} />
                                      Video
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div
                                  key={evidence.id}
                                  className="group relative aspect-square rounded-lg overflow-hidden bg-slate-200 border border-slate-300"
                                >
                                  <img
                                    src={url}
                                    alt={evidence.notes || 'Bukti'}
                                    className="w-full h-full object-cover"
                                    onError={(e: any) => {
                                      e.target.src =
                                        'https://via.placeholder.com/150?text=Gambar+Rusak';
                                    }}
                                  />
                                  {evidence.notes && (
                                    <div className="absolute inset-0 bg-slate-900/80 p-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-center">
                                      <p className="text-white text-xs font-medium line-clamp-3">
                                        {evidence.notes}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}
